import { api, type DashboardResponse, type DueItem } from '../api';
import {
  arcMeter,
  errorCard,
  esc,
  fmtDate,
  fmtHours,
  fmtInt,
  html,
  loading,
  pill,
  REGIME_LABEL,
  statusLabel,
  statusState,
  STRIPE,
  tile,
  type State,
} from '../ui';

const dueState = (s: DueItem['state']): State => (s === 'OVERDUE' ? 'OVERDUE' : s === 'DUE_SOON' ? 'DUE_SOON' : 'OK');
const dueLabel = (s: DueItem['state']): string =>
  s === 'OVERDUE' ? 'Overdue' : s === 'DUE_SOON' ? 'Due soon' : 'In tolerance';

export function dashboardSkeleton(): string {
  return html`
    <div class="phead">
      <div>
        <h1>Fleet airworthiness</h1>
        <p>Loading the current position…</p>
      </div>
    </div>
    ${loading('Reading the fleet')}
  `;
}

export async function renderDashboard(host: HTMLElement): Promise<void> {
  host.innerHTML = dashboardSkeleton();

  let stats: DashboardResponse;
  let due: DueItem[];
  try {
    [stats, due] = await Promise.all([api.dashboard(), api.dueList()]);
  } catch (e) {
    host.innerHTML = errorCard(
      e instanceof Error ? e.message : 'Could not load the dashboard.',
      'reload',
    );
    host.querySelector('[data-action=reload]')?.addEventListener('click', () => renderDashboard(host));
    return;
  }

  const asOf = new Date(stats.asOf);
  const grounded = due.find((d) => d.state === 'OVERDUE');

  host.innerHTML = html`
    <div class="phead">
      <div>
        <h1>Fleet airworthiness</h1>
        <p>
          ${fmtInt(stats.fleet.total)} aircraft ·
          ${esc(['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][asOf.getUTCDay()])}, ${esc(fmtDate(asOf.toISOString()))} · rolling review window 90 days
        </p>
      </div>
      <div style="display:flex;gap:8px">
        <button class="btn" type="button" data-report="fleet-status">Export status report</button>
        <button class="btn pri" type="button" data-dialog="work-pack">New work pack</button>
      </div>
    </div>

    <div class="tiles">
      ${tile({ label: 'Airworthy', value: stats.fleet.airworthy, sub: `of ${stats.fleet.total} aircraft`, tone: 'ok' })}
      ${tile({
        label: 'Grounded',
        value: stats.fleet.grounded,
        sub: grounded ? `${grounded.registration} · ${grounded.task.slice(0, 34)}` : 'none',
        tone: stats.fleet.grounded ? 'exceed' : 'ok',
      })}
      ${tile({
        label: 'Overdue items',
        value: stats.due.overdue,
        sub: stats.due.components.overdue
          ? `${stats.due.components.overdue} of them component lives`
          : 'past the tolerance band',
        tone: stats.due.overdue ? 'exceed' : 'ok',
      })}
      ${tile({
        label: 'Due soon',
        value: stats.due.dueSoon,
        sub: stats.due.components.dueSoon
          ? `${stats.due.components.dueSoon} of them component lives`
          : 'within 30 days or 25 hours',
        tone: stats.due.dueSoon ? 'caution' : 'ok',
      })}
      ${tile({
        label: 'ARC expiring ≤ 90 d',
        value: stats.arcsExpiringWithin90Days,
        sub: 'review window open',
        tone: stats.arcsExpiringWithin90Days ? 'caution' : 'ok',
      })}
      ${tile({
        label: 'Open defects',
        value: stats.defects.open,
        sub: `${stats.defects.grounding} grounding`,
        tone: stats.defects.grounding ? 'exceed' : 'acc',
      })}
      ${tile({
        label: 'AD compliance',
        value: stats.directives.compliancePercent,
        suffix: '%',
        sub: `${stats.directives.overdue} of ${stats.directives.applicable} applicable overdue`,
        tone: stats.directives.overdue ? 'caution' : 'acc',
      })}
    </div>

    <div class="card">
      <div class="card-h">
        <div>
          <h2>Due list</h2>
          <p class="tiny dim">
            Overdue first, then by remaining margin — projected on rolling utilisation
          </p>
        </div>
        <span class="lbl">${fmtInt(due.length)} items</span>
      </div>
      <div class="scrollx">
        <table class="tbl">
          <thead>
            <tr>
              <th>Aircraft</th>
              <th class="wide">Task</th>
              <th class="col-opt">Due</th>
              <th>Margin remaining</th>
              <th>State</th>
            </tr>
          </thead>
          <tbody>
            ${due.length === 0
              ? html`<tr><td colspan="5" class="muted">Nothing due in the forecast window.</td></tr>`
              : due
                  .map(
                    (d) => html`
                      <tr class="click" data-aircraft="${esc(d.aircraftId)}">
                        <td class="st ${STRIPE[dueState(d.state)]} n">
                          <span class="reg">${esc(d.registration)}</span>
                          <span class="sub2">${esc(d.typeModel)}</span>
                        </td>
                        <td class="wide">
                          ${esc(d.task)}
                          <span class="sub2">
                            ${/* A component's life and a programme task both
                                  read as "due" and are cleared in different
                                  places, so the row names which it is rather
                                  than leaving the part number to imply it. */
                              d.kind === 'COMPONENT' ? 'Component · ' : ''}${esc(d.source || '—')}
                          </span>
                        </td>
                        <td class="n col-opt">
                          ${d.dueTsnHours !== null
                            ? `at ${fmtHours(d.dueTsnHours)} h`
                            : fmtDate(d.dueAt)}
                          ${d.projectedDueAt && d.dueTsnHours !== null
                            ? html`<span class="sub2">≈ ${esc(fmtDate(d.projectedDueAt))}</span>`
                            : ''}
                        </td>
                        <td>${arcMeter(d.remaining, d.interval, d.tolerance, d.drivingUnit)}</td>
                        <td>${pill(dueState(d.state), dueLabel(d.state))}</td>
                      </tr>
                    `,
                  )
                  .join('')}
          </tbody>
        </table>
      </div>
      <div class="card-f">
        Green band = normal, yellow = inside tolerance, past the mark = exceeded. The needle shows
        consumed interval.
      </div>
    </div>
  `;
}

export async function renderFleet(host: HTMLElement, archived = false): Promise<void> {
  host.innerHTML = html`
    <div class="phead"><div><h1>Fleet</h1><p>Loading…</p></div></div>
    ${loading('Reading the fleet')}
  `;

  try {
    // Both lists are read: the archived count is worth showing even when it is
    // zero-weight, because an aircraft archived by mistake is otherwise
    // invisible and there is no way back to it.
    const [fleet, past] = await Promise.all([api.fleet(archived), archived ? null : api.fleet(true)]);
    const archivedCount = archived ? fleet.length : (past?.length ?? 0);

    host.innerHTML = html`
      <div class="phead">
        <div>
          <h1>Fleet</h1>
          <p>
            ${archived
              ? `${fmtInt(fleet.length)} aircraft management has ended for · records retained under ML.A.305`
              : `${fmtInt(fleet.length)} aircraft · the regulatory basis of each is recorded, not assumed`}
          </p>
        </div>
        <button class="btn pri" type="button" data-dialog="add-aircraft">Add aircraft</button>
      </div>

      <div class="card">
        <div class="card-h">
          <h2>${archived ? 'Off the fleet' : 'Managed aircraft'}</h2>
          ${archived
            ? html`<button class="btn sm" type="button" data-fleet-view="managed">Back to managed</button>`
            : archivedCount
              ? html`<button class="btn sm" type="button" data-fleet-view="archived">Off the fleet (${fmtInt(archivedCount)})</button>`
              : html`<span class="lbl">sorted by state</span>`}
        </div>
        <div class="scrollx">
          <table class="tbl">
            <thead>
              <tr>
                <th>Registration</th>
                <th class="wide">Type</th>
                <th>Regime / basis</th>
                <th>Airframe</th>
                <th class="col-opt">ARC</th>
                <th>State</th>
              </tr>
            </thead>
            <tbody>
              ${fleet
                .map(
                  (a) => html`
                    <tr class="click" data-aircraft="${esc(a.id)}">
                      <td class="st ${STRIPE[statusState(a.status)]} n">
                        <span class="reg" style="font-size:14px">${esc(a.registration)}</span>
                        <span class="sub2">${esc(a.serialNumber)}</span>
                      </td>
                      <td class="wide">
                        ${esc(a.type)}
                        <span class="sub2">${esc(a.year ?? '')} · ${esc(a.base ?? '')}</span>
                      </td>
                      <td>
                        <span class="chip acc">${esc(REGIME_LABEL[a.regime] ?? a.regime)}</span>
                        <span class="sub2" title="${esc(a.regimeJustification ?? '')}">
                          ${esc(a.approval ?? '—')}
                        </span>
                      </td>
                      <td class="n">
                        ${fmtHours(a.tsnHours)} h<span class="sub2">${fmtInt(a.landings)} ldg</span>
                      </td>
                      <td class="n col-opt">
                        ${a.arc ? fmtDate(a.arc.expiresAt) : '—'}
                        <span class="sub2">
                          ${a.arc ? `${a.arc.formKind.replace('FORM_', 'Form ').toLowerCase()} · ${a.arc.extensionsUsed} of 2` : 'no ARC'}
                        </span>
                      </td>
                      <td>
                        ${a.archivedAt
                          ? html`${pill('NA', 'Off the fleet')}
                             <span class="sub2">management ended ${fmtDate(a.archivedAt)}</span>`
                          : html`${pill(statusState(a.status), statusLabel(a.status))}
                             <span class="sub2">${esc(a.groundedReason ?? a.headline)}</span>`}
                      </td>
                    </tr>
                  `,
                )
                .join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;

    host.querySelectorAll<HTMLElement>('[data-fleet-view]').forEach((b) =>
      b.addEventListener('click', () => void renderFleet(host, b.dataset.fleetView === 'archived')),
    );
  } catch (e) {
    host.innerHTML = errorCard(e instanceof Error ? e.message : 'Could not load the fleet.', 'reload');
    host.querySelector('[data-action=reload]')?.addEventListener('click', () => renderFleet(host, archived));
  }
}
